import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Copy, Check } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { accountKey } from './accountsFormat';
import './AccountsTrapsTab.css';

/*
 * AccountsTrapsTab — read-only port of the GAS TrapQuestionsPanel for
 * /accounts: Command-of-the-Message trap-setting questions per
 * (agency, incumbent). Each question is a three-part trap — SET the
 * anchor the buyer believes, OPEN the gap against their current tech,
 * CLOSE on a quantified/named-risk consequence — with a per-question
 * copy button. Editing/regeneration stay in GAS for now.
 * Data: GET /api/v1/trap-questions/:account?scope=account[&competitor=]
 * → { exists, set, competitors }.
 */

const TQ_PARTS = [
  { field: 'set_anchor', label: 'Set — anchor' },
  { field: 'open_gap', label: 'Open — gap' },
  { field: 'close_consequence', label: 'Close — consequence' },
];

const pct = (c) => `${Math.round((Number(c) || 0) * 100)}%`;

function questionText(q) {
  return [
    q.trap_name,
    q.weakness ? `Exposes: ${q.weakness}` : null,
    `Set (anchor): ${q.set_anchor}`,
    `Open (gap): ${q.open_gap}`,
    `Close (consequence): ${q.close_consequence}`,
    q.why_it_works ? `Why this works: ${q.why_it_works}` : null,
    q.if_pushback ? `If they push back: ${q.if_pushback}` : null,
  ].filter(Boolean).join('\n');
}

export default function AccountsTrapsTab({ selectedDepartment }) {
  const account = accountKey(selectedDepartment);
  const [competitor, setCompetitor] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-trap-questions', account, competitor],
    queryFn: () => apiClient.get(`/api/v1/trap-questions/${encodeURIComponent(account)}`, {
      params: { scope: 'account', ...(competitor ? { competitor } : {}) },
    }),
    enabled: !!account,
    staleTime: 60_000,
  });

  const set = data?.exists ? data.set : null;
  const activeCompetitor = set?.competitor || competitor;

  // Distinct incumbent options: the resolved set's incumbent + others on file.
  const compOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    if (activeCompetitor) { seen.add(activeCompetitor); out.push(activeCompetitor); }
    (data?.competitors || []).forEach((c) => {
      if (c.competitor && !seen.has(c.competitor)) { seen.add(c.competitor); out.push(c.competitor); }
    });
    return out;
  }, [activeCompetitor, data?.competitors]);

  const copyQuestion = (q) => {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(questionText(q)).then(() => {
      setCopiedId(q.id);
      setTimeout(() => setCopiedId((cur) => (cur === q.id ? null : cur)), 2000);
    }, () => {});
  };

  if (isLoading) return <div className="accounts-panel-note">Loading trap questions…</div>;
  if (error) {
    return (
      <div className="accounts-error">
        <AlertCircle size={16} /> {error.message || 'Failed to load trap questions'}
      </div>
    );
  }

  const questions = set?.questions || [];
  const summary = set?.assessment?.summary || '';
  const weakSignal = set?.assessment?.weak_competitive_signal;

  return (
    <div className="accounts-traps">
      {compOptions.length > 0 && (
        <div className="tq-controls">
          <label className="tq-select-label" htmlFor="tq-incumbent">Incumbent</label>
          <select
            id="tq-incumbent"
            className="tq-select"
            value={activeCompetitor || ''}
            onChange={(e) => setCompetitor(e.target.value)}
          >
            {compOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {!set && (
        <div className="ts-card">
          <p className="accounts-panel-note">
            No trap questions generated for this agency yet
            {competitor ? ` vs ${competitor}` : ''}. Generation is owner-run
            (no in-app AI spend) — they will appear here once the owner batch covers this account.
          </p>
        </div>
      )}

      {set && (
        <>
          <div className="tq-meta">
            <span className={`tq-badge ${set.status === 'curated' ? 'tq-badge--curated' : 'tq-badge--ai'}`}>
              {set.status === 'curated' ? 'Curated' : 'AI draft'}
            </span>
            <span className="tq-vs">vs <strong>{set.competitor}</strong></span>
            {weakSignal && (
              <span className="tq-badge tq-badge--warn" title="Incumbent barely present / thin data — traps are lower-confidence">
                weak signal
              </span>
            )}
            {set.stale && (
              <span className="tq-badge tq-badge--warn" title="Source data changed since generation">stale</span>
            )}
            <span className="tq-meta-note">
              {pct(set.assessment?.overall_confidence)} confidence · {set.persona || 'CISO / IT leadership'} · {set.generatedAt ? String(set.generatedAt).slice(0, 10) : 'n/a'} · read-only (edit in the GAS dashboard)
            </span>
          </div>
          {summary && <p className="tq-summary">{summary}</p>}

          <div className="tq-list">
            {questions.map((q, i) => (
              <div className="ts-card tq-card" key={q.id || i}>
                <div className="tq-head">
                  <span className="tq-num">{i + 1}</span>
                  <div className="tq-head-main">
                    <h3 className="ts-card-title">
                      {q.trap_name}
                      {q.curated && <span className="tq-tag">curated</span>}
                    </h3>
                    {q.weakness && <div className="tq-weakness">Exposes: {q.weakness}</div>}
                  </div>
                  <button
                    type="button"
                    className="tq-copy-btn"
                    onClick={() => copyQuestion(q)}
                    title="Copy this question"
                  >
                    {copiedId === q.id ? <Check size={13} /> : <Copy size={13} />}
                    <span>{copiedId === q.id ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>

                {TQ_PARTS.map(({ field, label }) => (
                  <div className={`tq-part tq-part--${field}`} key={field}>
                    <div className="tq-part-label">{label}</div>
                    <p className="tq-part-text">{q[field]}</p>
                  </div>
                ))}

                {q.why_it_works && (
                  <p className="tq-note"><strong>Why this works</strong> {q.why_it_works}</p>
                )}
                {q.if_pushback && (
                  <p className="tq-note"><strong>If they push back</strong> {q.if_pushback}</p>
                )}
              </div>
            ))}
            {questions.length === 0 && (
              <p className="accounts-panel-note">This set has no questions.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
